/**
 * Value-area expansion — shared by every price-profile family.
 *
 * A "value area" is the contiguous band of price rows around the point of
 * control that accounts for some target share (conventionally 70%) of the
 * profile's total. The algorithm is identical whether the rows are counting
 * VOLUME (volume profile) or TIME brackets (TPO / market profile): start at the
 * POC and repeatedly absorb whichever adjacent row holds more, until the target
 * is covered.
 *
 * Extracted so the two profile families share ONE implementation. The counter
 * being summed differs; the band-growing logic must not.
 */

export interface ValueAreaBounds {
  /** Lowest-priced row inside the value area. */
  valIdx: number
  /** Highest-priced row inside the value area. */
  vahIdx: number
}

/**
 * Grow a value area outward from `pocIdx` until the covered total reaches
 * `target`, taking the LARGER neighbor at each step (ties → the upper side).
 *
 * `totals[i]` is row i's magnitude, ascending by price. Stops early when both
 * edges are exhausted, so a `target` above the achievable sum yields the whole
 * range rather than looping.
 */
export function expandValueArea(
  totals: readonly number[],
  pocIdx: number,
  target: number,
): ValueAreaBounds {
  const rows = totals.length
  if (rows === 0) return { valIdx: 0, vahIdx: 0 }
  const poc = Math.min(Math.max(pocIdx, 0), rows - 1)

  let valIdx = poc
  let vahIdx = poc
  let covered = totals[poc]
  while (covered < target && (valIdx > 0 || vahIdx < rows - 1)) {
    // -1 marks an exhausted side, so it always loses to a real neighbor.
    const belowVol = valIdx > 0 ? totals[valIdx - 1] : -1
    const aboveVol = vahIdx < rows - 1 ? totals[vahIdx + 1] : -1
    if (aboveVol >= belowVol) {
      vahIdx += 1
      covered += totals[vahIdx]
    } else {
      valIdx -= 1
      covered += totals[valIdx]
    }
  }
  return { valIdx, vahIdx }
}

/** Index of the highest-magnitude row — the point of control. Ties → lowest
 *  index, matching the "first bucket at the max" convention. */
export function pointOfControl(totals: readonly number[]): number {
  let pocIdx = 0
  for (let i = 1; i < totals.length; i++) {
    if (totals[i] > totals[pocIdx]) pocIdx = i
  }
  return pocIdx
}
